import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {makeStyles} from '@material-ui/core';
import {AutoSizer, List} from 'react-virtualized';
import {useDispatch, useSelector} from 'react-redux';
import useAssaysFilter from "./hooks/useAssaysFilter";
import {
    getCatalog,
    getPunkts,
    setCatalogFilteredValue,
    setSelectedComplex,
} from '@cabinet-packages/redux';
import {Link, useLocation} from "react-router-dom";
import {isIncludedInComplexResearch} from "./helpers/isIncludedInComplexResearch";
import {isIncludedInOrderingList} from "./helpers/isIncludedInOrderingList";
import AddRemoveIconButtons from "./components/AddRemoveIconButtons";
import Loading from "./components/Loading";
import {getAssayRowHeight} from "./helpers/getAssayRowHeight";
import {STORAGE_KEYS, EXCLUDED_PANEL_IDS} from "./helpers/constants";
import PageHeader from "../../components/PageHeader/PageHeader";
import PanelSelector from "../../components/PanelSelector/PanelSelector";
import {designTokens} from '../../styles/designTokens';

const useStyles = makeStyles((theme) => ({
    root: {
        width: '100%',
        maxWidth: '100%',
        minHeight: '100%',
        display: 'grid',
        gridTemplateRows: 'auto 1fr',
    },
    contentWrapper: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        overflow: 'hidden',
        flex: 1,
        padding: `0 ${designTokens.spacing.lg} ${designTokens.spacing.lg}`,
        '@media (max-width: 640px)': {
            padding: `${designTokens.spacing.md} ${designTokens.spacing.sm} ${designTokens.spacing.sm}`,
        },
        '@media (max-width: 600px)': {
            paddingTop: '0',
        },
    },
    tableWrapper: {
        display: 'grid',
        gridTemplateRows: 'auto 1fr',
        minHeight: 0,
        flex: 1,
        width: '100%',
        maxWidth: '1200px',
        margin: '0 auto',
    },
    listHeadersContainer: {
        background: designTokens.colors.table.headerBg,
        display: 'grid',
        gridTemplateColumns: '1fr 100px 120px 100px 60px',
        border: '1px solid transparent',
        boxShadow: '0 0 1px 1px rgba(0, 0, 0, 0.1)',
        boxSizing: 'border-box',
        overflow: 'hidden',
        minHeight: "50px",
        borderTopLeftRadius: '10px',
        borderTopRightRadius: '10px',
        '@media (max-width: 900px)': {
            gridTemplateColumns: '1fr 80px 80px 50px',
        },
    },
    listHeader: {
        display: 'flex',
        justifyContent: 'center',
        textAlign: 'center',
        alignItems: 'center',
        position: 'relative',
        borderLeft: '1px solid transparent',

        '& p': {
            ...designTokens.typography.tableHeader,
            margin: 0,
            width: '100%',
            textAlign: 'center',
            '@media (max-width: 600px)': {
                fontSize: '15px',
                lineHeight: '20px',
            },
        },
    },
    listHeaderHiddenOnMobile: {
        display: 'flex',
        justifyContent: 'center',
        textAlign: 'center',
        alignItems: 'center',
        position: 'relative',
        borderLeft: '1px solid transparent',

        '& p': {
            ...designTokens.typography.tableHeader,
            margin: 0,
            width: '100%',
            textAlign: 'center',
            '@media (max-width: 600px)': {
                fontSize: '15px',
                lineHeight: '20px',
            },
        },
        '@media (max-width: 900px)': {
            display: 'none',
        },
    },
    listHeaderAssayName: {
        display: 'flex',
        justifyContent: 'flex-start',
        flexDirection: 'row',
        textAlign: 'center',
        alignItems: 'center',
        position: 'relative',
        paddingLeft: '8px',

        '& p': {
            ...designTokens.typography.tableHeader,
            '@media (max-width: 600px)': {
                fontSize: '15px',
                lineHeight: '20px',
            },
        },
    },
    listHeaderTinyText: {
        fontSize: '10px',
    },
    list: {
        scrollBehavior: 'smooth',
        boxShadow: '0 0 1px 1px rgba(0, 0, 0, 0.1)',
        '&:focus': {
            outline: 'none',
        },
    },
    listItem: {
        display: 'grid',
        gridTemplateColumns: '1fr 100px 120px 100px 60px',
        textAlign: 'center',
        minWidth: 0,

        '& *:not(svg):not(path):not(ol):not(li)': {
            ...designTokens.typography.tableRow,
            '@media (max-width: 600px)': {
                fontSize: '14px',
                lineHeight: '18px',
                letterSpacing: '-0.31px',
            },
        },
        '@media (max-width: 900px)': {
            gridTemplateColumns: '1fr 80px 80px 50px',
        },
    },
    listItemTitle: {
        height: 'inherit',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-start',
        alignItems: 'center',
        padding: '5px',
        textAlign: 'start',
        transition: '.5s',
        color: designTokens.colors.table.rowText,
        minWidth: 0,
        overflow: 'hidden',

        '& p': {
            margin: 0,
            wordBreak: 'break-word',
            overflowWrap: 'anywhere',
            display: '-webkit-box',
            WebkitLineClamp: 7,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            '@media (max-width: 600px)': {
                WebkitLineClamp: 10,
            },
        },

        '&:hover': {
            color: theme.palette.primary.main,
            transition: '.5s',
        },

        '@media (max-width: 1140px)': {
            fontSize: '14px',
        },
    },
    listItemTitleForComplex: {
        height: 'inherit',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        padding: '5px',
        textAlign: 'start',
        transition: '.5s',
        color: designTokens.colors.table.rowText,
        minWidth: 0,
        overflow: 'hidden',

        '& > p': {
            margin: 0,
            wordBreak: 'break-word',
            overflowWrap: 'anywhere',
        },

        '&:hover': {
            color: theme.palette.primary.main,
            transition: '.5s',
        },

        '@media (max-width: 1140px)': {
            fontSize: '14px',
        },
    },
    listItemGroupTitle: {
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-around',
        height: '100%',
        padding: '5px',

        '& p': {
            ...designTokens.typography.tableHeader,
            letterSpacing: '0',
        },

        '@media (max-width: 1140px)': {
            padding: '5px 5px 5px 5%',
        },
    },
    listItemTime: {
        height: 'inherit',
        borderLeft: '1px solid rgba(0, 0, 0, 0.1)',
        padding: '5px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-around',
    },
    listItemComplex: {
        height: 'inherit',
        borderLeft: '1px solid rgba(0, 0, 0, 0.1)',
        padding: '5px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        '@media (max-width: 900px)': {
            display: 'none',
        },
    },
    listItemPrice: {
        height: 'inherit',
        borderLeft: '1px solid rgba(0, 0, 0, 0.1)',
        display: 'flex',
        userSelect: 'none',
        padding: '5px',
        flexDirection: 'column',
        justifyContent: 'space-around',
    },
    listItemHeaderOfGroup: {
        background: designTokens.colors.table.sectionBg,
        color: designTokens.colors.white,
    },
    addRemoveButton: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        borderLeft: '1px solid rgba(0, 0, 0, 0.1)',
    },
    errorMessage: {
        textAlign: 'center',
    },
    listForComplexFillings: {
        listStylePosition: 'inside',
        margin: '4px 0 0 0',
        padding: 0,
    },
    assaysNamesInComplex: {
        fontSize: '13px',
        color: designTokens.colors.grey[400],
        lineHeight: '17px',
    },
}));

export default function Catalog() {
    const classes = useStyles();
    const dispatch = useDispatch();
    const location = useLocation();
    const { orderingList } = useSelector(state => state.orderAssays);
    const {catalog, complexResearch, takeId, complexId, filteredValue, complexTransition} = useSelector(state => state.assaysCatalog);
    const [isLoadingCatalog, setIsLoadingCatalog] = useState(false);
    const listRef = useRef();
    const itemRef = useRef();
    const {punkts} = useSelector(store => store.collectionPoints);
    const [isUserType, setIsUserType] = useState(false);
    const isDoctor = localStorage.getItem(STORAGE_KEYS.IS_DOCTOR);
    const [filteredPanels, setFilteredPanels] = useState([]);
    const [selectedPanel, setSelectedPanel] = useState(null);

    const catalogWithoutTakes = useMemo(() => {
        if (!catalog || !complexResearch || typeof catalog === "string" || typeof complexResearch === "string") {
            return null;
        }

        const seen = new Set();
        return [
            ...catalog,
            {
                idParent: 0,
                superGroupName: 'Комплексні дослідження.Комплексні дослідження',
                name: 'Комплексні дослідження'
            },
            ...complexResearch
        ]
            .filter(item => {
                // Always keep group headers
                if (+item.idParent === 0) return true;
                const key = `${item.type}_${item.id}`;
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
            })
            .map((item, idx) => ({
                ...item,
                normIndexLocalStorageKey: idx,
            }));
    }, [catalog, complexResearch]);

    const measuredRef = useCallback(node => {
        if (complexTransition && node) {
            node.scrollIntoView({
                block: 'start',
            });
        }
    }, [complexTransition]);

    const {
        filteredState,
        filterValue,
        handleChangeFilterValue,
    } = useAssaysFilter(catalogWithoutTakes);

    const handleSearchTextFieldValue = (event) => {
        handleChangeFilterValue(event);
        dispatch(setCatalogFilteredValue(event.target.value));
        if (!isUserType) setIsUserType(true);
    }

    const lastViewedId = localStorage.getItem(STORAGE_KEYS.LAST_VIEWED_ID);
    const scrollIndex = useMemo(() => {
        if (!lastViewedId || !filteredState.length) return 0;
        const index = filteredState.findIndex(item => String(item.id) === lastViewedId);
        return index >= 0 ? index : 0;
    }, [lastViewedId, filteredState]);

    useEffect(() => {
        if (filteredValue && !isUserType && catalog && filteredState) {
            handleChangeFilterValue({target: {value: filteredValue}})
            setIsUserType(true);
        }
    }, [filteredValue, isUserType, catalog, filteredState]);

    useEffect(() => {
        if (!punkts) dispatch(getPunkts());
    }, [dispatch, punkts]);

    useEffect(() => {
        if (!catalog && takeId && complexId && !isLoadingCatalog) dispatch(getCatalog());
    }, [catalog, takeId, dispatch, complexId, isLoadingCatalog]);

    useEffect(() => {
        if (catalog && typeof catalog !== "string" && catalogWithoutTakes) {
            const panels = catalogWithoutTakes
                .filter(item => item.idParent === 0)
                .filter(item => !EXCLUDED_PANEL_IDS.includes(item.id))
                .slice(0, 12);

            setFilteredPanels(panels);

            if (!selectedPanel) {
                const savedPanelId = localStorage.getItem(STORAGE_KEYS.SELECTED_PANEL);
                if (savedPanelId && savedPanelId !== 'all') {
                    const savedPanel = panels.find(p => p.id === +savedPanelId);
                    if (savedPanel) {
                        setSelectedPanel(savedPanel);
                    }
                }
            }
        }
    }, [catalog, catalogWithoutTakes]);

    const handleChangePanel = (event, newPanel) => {
        setSelectedPanel(newPanel);
        localStorage.setItem(STORAGE_KEYS.SELECTED_PANEL, newPanel ? newPanel.id : '');

        setTimeout(() => {
            if (listRef.current) {
                if (!newPanel) {
                    const storedLastViewedId = localStorage.getItem(STORAGE_KEYS.LAST_VIEWED_ID);
                    if (storedLastViewedId && filteredState.length) {
                        const index = filteredState.findIndex(item => String(item.id) === storedLastViewedId);
                        listRef.current.scrollToRow(index >= 0 ? index : 0);
                    } else {
                        listRef.current.scrollToRow(0);
                    }
                } else {
                    const panelIndex = filteredState.findIndex(item =>
                        item.id === newPanel.id && item.idParent === 0
                    );
                    if (panelIndex !== -1) {
                        listRef.current.scrollToRow(panelIndex);
                    }
                }
            }
        }, 100);
    };

    useLayoutEffect(() => {
        if (listRef.current) {
            listRef.current.recomputeRowHeights();
            listRef.current.forceUpdateGrid();
        }
    }, [filteredState, filterValue]);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (listRef.current) {
                listRef.current.recomputeRowHeights();
                listRef.current.forceUpdateGrid();
            }
        }, 0);
        return () => clearTimeout(timer);
    }, [catalogWithoutTakes, location.key]);

    useEffect(() => {
        const handleResize = () => {
            if (listRef.current) {
                listRef.current.recomputeRowHeights();
                listRef.current.forceUpdateGrid();
            }
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const rowRenderer = useCallback((props) => {
        const { key, index, style } = props;
        const item = filteredState[index];

        const storedLastViewedId = localStorage.getItem(STORAGE_KEYS.LAST_VIEWED_ID);
        const isLastViewed = storedLastViewedId && String(item.id) === storedLastViewedId;

        const newStyles = {
            ...style,
            background: isIncludedInOrderingList(item, orderingList)
                ? designTokens.colors.table.selectedRowBg
                : 'none',
            borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
        };

        if (isLastViewed) newStyles.borderBottom = '3px solid #e4002b88';

        const saveIndexToLocalStore = () => {
            if (item.type === 'complex') dispatch(setSelectedComplex(item.name));
            localStorage.setItem(STORAGE_KEYS.NORM_INDEX, item.normIndexLocalStorageKey);
            localStorage.setItem(STORAGE_KEYS.LAST_VIEWED_ID, String(item.id));
        };

        if (item.price === 0) {
            return <div key={key} style={{...style, height: 0}} />;
        }

        return (
            +item.idParent !== 0
                ? <Link
                    key={key}
                    ref={itemRef}
                    onClick={saveIndexToLocalStore}
                    style={{
                        textDecoration: 'none',
                        color: 'inherit',
                    }}
                    to={{
                        pathname: item.type === 'complex' ? `/complex-info/${item.id}` : `/assay-info/${item.id}`,
                    }}>
                    <div style={newStyles} className={classes.listItem}>
                        <div className={item.type === 'complex'
                            ? classes.listItemTitleForComplex
                            : classes.listItemTitle
                        }>
                            <p style={{overflow: 'hidden'}}>
                                {item.name}
                            </p>
                            {item.type === 'complex' && (
                                <ol className={classes.listForComplexFillings}>
                                    {[...(item.filling || []), ...(item.idGrTest || [])].map((fill, idx) => (
                                        <li key={fill.id + ' ' + idx} className={classes.assaysNamesInComplex}>{fill.name}</li>
                                    ))}
                                </ol>
                            )}
                        </div>
                        <div className={classes.listItemTime}>
                            {item.duration_day}
                        </div>
                        <div className={classes.listItemComplex}>
                            {isIncludedInComplexResearch(item, complexResearch) ? '+' : '-'}
                        </div>
                        <div className={classes.listItemPrice}>
                            {isDoctor ? item.price : item.price - item.price * item.discount / 100}
                        </div>
                        <div className={classes.addRemoveButton}>
                            {+item.idParent !== +takeId && !!item.price && <AddRemoveIconButtons
                                item={item}
                                isIncludedInOrderingList={isIncludedInOrderingList(item, orderingList)}
                            />}
                        </div>
                    </div>
                </Link>
                : <div
                    key={key}
                    style={{...newStyles, background: designTokens.colors.table.sectionBg}}
                >
                    <div className={classes.listItemGroupTitle}>
                        <p style={{overflow: 'hidden'}}>{item.superGroupName.split('.')[1] ? item.superGroupName.split('.')[1] : item.superGroupName.split('.')[0]}</p>
                    </div>
                </div>
        );
    }, [filteredState, orderingList, dispatch, classes, complexResearch, takeId, isDoctor]);

    const noRowRenderer = useCallback(() => (
        <div style={{background: designTokens.colors.table.sectionBg}}>
            <div className={classes.listItemGroupTitle}>
                <p>Вибачте, нічого не знайдено.</p>
            </div>
        </div>
    ), [classes.listItemGroupTitle]);

    if ((!catalogWithoutTakes || isLoadingCatalog) && typeof catalog !== "string") {
        return <Loading/>
    } else if (typeof catalog === "string") {
        return <div className={classes.errorMessage}>Вибачте, сталась помилка під час завантаження.</div>
    }

    return (
        <div className={classes.root} ref={measuredRef}>
            <PageHeader
                title="Перелік досліджень"
                subtitle="Пошук та вибір лабораторних аналізів"
                searchInput={{
                    value: filterValue,
                    onChange: handleSearchTextFieldValue,
                    placeholder: "Пошук досліджень",
                }}
                controls={filteredPanels.length > 0 ? (
                    <PanelSelector
                        value={selectedPanel}
                        options={filteredPanels}
                        onChange={handleChangePanel}
                    />
                ) : null}
            />
            <div className={classes.contentWrapper}>
                <div className={classes.tableWrapper}>
                    <div className={classes.listHeadersContainer}>
                        <div className={classes.listHeaderAssayName}>
                            <p>
                                Назва дослідження
                            </p>
                        </div>
                        <div className={classes.listHeader}>
                            <p>
                                Термін виконання,
                                <br/>
                                <span className={classes.listHeaderTinyText}> робочих днів</span>
                            </p>
                        </div>
                        <div className={classes.listHeaderHiddenOnMobile}>
                            <p>
                                Дослідження у комплексі
                            </p>
                        </div>
                        <div className={classes.listHeader}>
                            <p>
                                Ціна, грн
                            </p>
                        </div>
                        <div className={classes.listHeader}>
                        </div>
                    </div>
                    <div>
                        <AutoSizer>
                            {({ height, width }) => (
                                <List
                                    key={`${filteredState.length}-${width}`}
                                    subheader={<div />}
                                    className={classes.list}
                                    width={width}
                                    height={height}
                                    ref={listRef}
                                    scrollToIndex={scrollIndex}
                                    noRowsRenderer={noRowRenderer}
                                    scrollToAlignment="start"
                                    rowCount={filteredState.length}
                                    rowHeight={({ index }) => getAssayRowHeight(filteredState[index], width, takeId)}
                                    rowRenderer={rowRenderer}
                                />)}
                        </AutoSizer>
                    </div>
                </div>
            </div>
        </div>
    );
}
